import {
  WebSocketEditNotification,
  WebSocketEditNotificationElement,
  WebSocketEditNotificationStatus,
} from '@agroone/entities'
import { inject, Injectable } from '@angular/core'
import { io, Socket } from 'socket.io-client'
import { API_URL } from '../models/environment.token'
import { LoggerService } from './logger.service'
import { WebSocketStateService } from './websocket-state.service'

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private logger = inject(LoggerService)
  private webSocketStateService = inject(WebSocketStateService)

  // Assigned by `connect()`, called from the constructor
  private socket!: Socket
  private apiUrl: string = inject(API_URL)

  constructor() {
    this.connect()

    // Close the WebSocket when the user closes the page
    window.onbeforeunload = () => {
      this.disconnect()
    }

    window.addEventListener('offline', () => {
      this.logger.warn('🚨 Network problem detected')
      this.socket.emit('offline')
    })

    window.addEventListener('online', () => {
      this.logger.info('✅ Network restored')
      this.socket.emit('online')
    })
  }

  private connect(): void {
    this.socket = io(this.apiUrl, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    })

    this.socket.on('connect', () => {
      this.logger.info('WebSocket connected')
      this.registerEventHandlers()
    })

    this.socket.on('disconnect', (reason) => {
      this.logger.error('WebSocket disconnected:', reason)
      setTimeout(() => {
        this.reconnect()
      }, 5000)
    })

    this.registerEventHandlers()
  }

  private registerEventHandlers(): void {
    this.socket.off(WebSocketEditNotificationStatus.EDITING)
    this.socket.off(WebSocketEditNotificationStatus.AVAILABLE)
    this.socket.off(WebSocketEditNotificationStatus.GET_EDITING_ELEMENTS)

    this.socket.on(WebSocketEditNotificationStatus.GET_EDITING_ELEMENTS, (data: WebSocketEditNotification[]) => {
      this.logger.info('Received WebSocket get editing elements message:', data)
      this.webSocketStateService.setMultipleEditingStatus(data)
    })

    this.socket.on(WebSocketEditNotificationStatus.EDITING, (data: WebSocketEditNotification[]) => {
      this.logger.info('Received WebSocket batch editing message:', data)
      this.webSocketStateService.setMultipleEditingStatus(data)
    })

    this.socket.on(WebSocketEditNotificationStatus.AVAILABLE, (data: WebSocketEditNotification[]) => {
      this.logger.info('Received WebSocket batch available message:', data)
      this.webSocketStateService.setMultipleEditingStatus(data)
    })

    this.socket.on('error', (error) => {
      this.logger.error('WebSocket error:', error)
      // Try to reconnect immediately in case of error
      this.reconnect()
    })
  }

  private disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
    }
  }

  public notifyEditing(
    notifications: Array<{
      elementId: string
      elementType: WebSocketEditNotificationElement
      force?: boolean
    }>,
  ): void {
    const batchNotification: WebSocketEditNotification[] = notifications.map((notification) => ({
      elementId: notification.elementId,
      status: WebSocketEditNotificationStatus.EDITING,
      elementType: notification.elementType,
      force: notification.force || false,
    }))

    this.socket.emit(WebSocketEditNotificationStatus.EDITING, batchNotification)
  }

  public notifyAvailable(
    notifications: Array<{
      elementId: string
      elementType: WebSocketEditNotificationElement
    }>,
  ): void {
    const batchNotification: WebSocketEditNotification[] = notifications.map((notification) => ({
      status: WebSocketEditNotificationStatus.AVAILABLE,
      elementId: notification.elementId,
      elementType: notification.elementType,
      force: false,
    }))

    this.socket.emit(WebSocketEditNotificationStatus.AVAILABLE, batchNotification)
  }

  public reconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
    }

    this.connect()
  }
}
